import "../index.scss";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import { Radio, Select, Space } from "antd";
import { GradeEnum } from "../../../types/npsParams";
import { NpsTypeEnum } from "../../../types/npsInfoParams";
import Rating from "./rating";
import { Translate } from "react-redux-i18n";
import { getCurrentTextTranslation } from "../../../helpers/getCurrentTextTranslation";
import { useActions } from "../../../hooks/useActions";
import RangeSlider from "../../../components/customSlider";

const NpsComponent: FC = () => {
  const { question, npsInfoParams, npsParams, isLoading } = useSelector((state: RootState) => {
    const question = state.questions?.question;
    const npsInfoParams = question?.answerTemplate?.npsInfoParams;
    return {
      answer: state.questions.answer?.answerValue,
      question: question,
      npsInfoParams: npsInfoParams,
      npsParams: npsInfoParams?.npsParams ?? [],
      isLoading: state.utils.isLoading,
    };
  });

  const { setUserAnswer } = useActions();
  const [answer, setAnswer] = useState<number>(0);
  const lowestScoreLabel = getCurrentTextTranslation(question?.answerTemplate?.textTranslationsToLowestScoreLabel);
  const highestScoreLabel = getCurrentTextTranslation(question?.answerTemplate?.textTranslationsToHighestScoreLabel);
  const lastChangeTime = useRef<number>(0);
  const changeTimeout = useRef<NodeJS.Timeout>();

  const onChange = useCallback(
    (value: number | undefined) => {
      if (isLoading) return;

      const now = Date.now();
      if (now - lastChangeTime.current < 300) {
        if (changeTimeout.current) {
          clearTimeout(changeTimeout.current);
        }
        changeTimeout.current = setTimeout(() => {
          lastChangeTime.current = now;
          const stringValue = value !== undefined ? String(value) : undefined;
          setAnswer(Number(value));
          setUserAnswer({ answerValue: stringValue, csiAnswers: [] });
        }, 300);
        return;
      }

      lastChangeTime.current = now;
      const stringValue = value !== undefined ? String(value) : undefined;
      setAnswer(Number(value));
      setUserAnswer({ answerValue: stringValue, csiAnswers: [] });
    },
    [setUserAnswer, isLoading],
  );

  useEffect(() => {
    return () => {
      if (changeTimeout.current) {
        clearTimeout(changeTimeout.current);
      }
    };
  }, []);

  const isType = useCallback((type: NpsTypeEnum) => npsInfoParams?.type === type, [npsInfoParams?.type]);

  useEffect(() => {
    if (changeTimeout.current) {
      clearTimeout(changeTimeout.current);
    }
    lastChangeTime.current = 0;
    setAnswer(0);
  }, [question?.questionId]);

  return (
    <div className="nps-template">
      <div style={{ opacity: isLoading ? 0.5 : 1, pointerEvents: isLoading ? "none" : "auto" }}>
        <div>
          {npsParams
            ?.sort((it) => (it?.grade === GradeEnum.MAX_GRADE ? 1 : -1))
            ?.map((it) => (
              <div key={it.grade} className="grade-text">
                <span style={{ fontFamily: question?.surveyDecor?.textFont }}>
                  <Translate value={it?.grade === GradeEnum.MAX_GRADE ? "max" : "min"} />
                </span>
                <span style={{ fontFamily: question?.surveyDecor?.textFont }}>
                  {getCurrentTextTranslation(it?.textTranslations)}
                </span>
              </div>
            ))}
        </div>
        {isType(NpsTypeEnum.SLIDER) ? (
          <>
            <RangeSlider min={1} max={npsInfoParams?.maxGrade ?? 10} value={answer} step={1} onChange={onChange} />
            <div className="lowest-highest-text-container">
              <span className="lowest-text" style={{ fontFamily: question?.surveyDecor?.textFont }}>
                {lowestScoreLabel ?? ""}
              </span>
              <span className="highest-text" style={{ fontFamily: question?.surveyDecor?.textFont }}>
                {highestScoreLabel ?? ""}
              </span>
            </div>
          </>
        ) : isType(NpsTypeEnum.RATING) ? (
          <div className="rating-container">
            {npsInfoParams?.icon && npsInfoParams?.maxGrade && (
              <div style={{ width: "100%" }}>
                <div style={{ marginBottom: 10 }}>
                  <Rating
                    onChange={(value) => onChange(value !== undefined ? Number(value) : undefined)}
                    iconType={npsInfoParams.icon}
                    maxValue={npsInfoParams.maxGrade}
                  />
                </div>
                <div className="lowest-highest-text-container">
                  <span className="lowest-text" style={{ fontFamily: question?.surveyDecor?.textFont }}>
                    {lowestScoreLabel ?? ""}
                  </span>
                  <span className="highest-text" style={{ fontFamily: question?.surveyDecor?.textFont }}>
                    {highestScoreLabel ?? ""}
                  </span>
                </div>
              </div>
            )}
          </div>
        ) : isType(NpsTypeEnum.DROPDOWN) ? (
          <Select
            size="large"
            onChange={(value) => onChange(Number(value))}
            value={answer ? String(answer) : undefined}
            placeholder={<Translate value="select" />}
            style={{ width: "98%", fontFamily: question?.surveyDecor?.textFont }}
          >
            {Array.from(Array(npsInfoParams?.maxGrade).keys())
              .reverse()
              .map((it, index) => (
                <Select.Option value={String(++it)} key={it}>
                  {it}
                  <span style={{ marginLeft: 15, fontSize: 13 }}>{index === 0 ? highestScoreLabel ?? "" : ""}</span>
                  <span style={{ fontSize: 13 }}>
                    {index === (question?.answerTemplate?.npsInfoParams?.maxGrade ?? 0) - 1
                      ? lowestScoreLabel ?? ""
                      : ""}
                  </span>
                </Select.Option>
              ))}
          </Select>
        ) : isType(NpsTypeEnum.RADIO_BUTTONS) ? (
          <Radio.Group value={answer} onChange={({ target }) => onChange(target.value)} size="large">
            <Space direction="vertical">
              {Array.from(Array(npsInfoParams?.maxGrade).keys())
                .reverse()
                .map((it, index) => (
                  <Radio value={++it} key={it}>
                    <span style={{ marginLeft: 25, fontFamily: question?.surveyDecor?.textFont }}>{it}</span>
                    <span style={{ marginLeft: 15, fontSize: 13 }}>{index === 0 ? highestScoreLabel ?? "" : ""}</span>
                    <span style={{ fontSize: 13 }}>
                      {index === (question?.answerTemplate?.npsInfoParams?.maxGrade ?? 0) - 1
                        ? lowestScoreLabel ?? ""
                        : ""}
                    </span>
                  </Radio>
                ))}
            </Space>
          </Radio.Group>
        ) : null}
      </div>
    </div>
  );
};

export default NpsComponent;
